/**
 * Writing to a sponsor by hand.
 *
 * Goes out whitelabeled as the client, on the same stream as their invitation,
 * so a sponsor never receives mail from two apparent senders about one thing.
 * It lands in the activity timeline like every other message, which is what
 * keeps the app the record of what was said rather than somebody's sent folder.
 *
 * ADDITIONAL CONTACTS ARE CHECKBOXES, not a single toggle. "Everyone on file"
 * is the right default and the wrong rule — the designer who left the agency
 * should not keep receiving mail, and the only person who knows that is the
 * one writing. Each is listed by name and address so the choice is informed
 * rather than a guess about who is behind a count.
 *
 * THE REMINDER HOLD DEFAULTS ON. A personal note followed by an automated
 * reminder the next morning makes the person who wrote it look absent, and it
 * is the sort of thing nobody notices until a sponsor mentions it.
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { T } from '../lib/copyEdit.tsx';

interface Template {
  name: string;
  subject: string;
  body: string;
}

const TOKEN = /\{[a-z_]+\}/g;

/**
 * One paragraph of the message, as the sponsor will receive it.
 *
 * `dropped` is the part worth having. The send path resolves a hand-written
 * message with the SAME function the automated emails use, and that function
 * leaves out any paragraph containing a field with no value — right for a
 * machine, where "Please send it by ." with nobody watching is worse than a
 * missing sentence, and wrong here, where somebody wrote the sentence and read
 * it back. Rather than change the rule and send a visible gap instead, the
 * preview shows what will actually happen before Send is pressed.
 */
interface PreviewParagraph {
  text: string;
  dropped: boolean;
}

/**
 * Resolves the body exactly as the server will.
 *
 * MIRRORS lib/mergeFields.ts resolveBody, deliberately and with the same rule,
 * because a preview that quietly disagreed with the send would be worse than
 * no preview: it would be a promise. messageFields.test.ts asserts this still
 * drops paragraphs, since that is the half somebody would be tempted to
 * simplify away.
 */
function previewBody(body: string, fields: Record<string, string | null>): PreviewParagraph[] {
  return body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => {
      let dropped = false;
      const text = paragraph.replace(TOKEN, (token) => {
        const value = fields[token.slice(1, -1)];
        if (value === undefined) return token;
        if (typeof value !== 'string' || !value.trim()) {
          dropped = true;
          return '';
        }
        return value;
      });
      /* The original is kept when it is going to be dropped, so what is shown
         struck through is what was written rather than a half-resolved ghost. */
      return { text: dropped ? paragraph : text, dropped };
    });
}

/** A subject is never dropped: it cannot be, so an absent value collapses and
 *  the spacing is tidied, which is what resolveSubject does. */
function previewSubject(subject: string, fields: Record<string, string | null>): string {
  return subject
    .replace(TOKEN, (token) => {
      const value = fields[token.slice(1, -1)];
      if (value === undefined) return token;
      return typeof value === 'string' && value.trim() ? value : '';
    })
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.:;!?])/g, '$1')
    .trim();
}

/** Splits text into plain runs and tokens, so the layer behind the box can
 *  mark the fields without touching anything else. */
function segments(text: string): { text: string; token: boolean }[] {
  const out: { text: string; token: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    if (m.index! > last) out.push({ text: text.slice(last, m.index), token: false });
    out.push({ text: m[0], token: true });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), token: false });
  return out;
}

export interface MessageContact {
  id: string;
  name: string;
  email: string;
  isPrimary: boolean;
}

export default function MessageSponsor({
  base,
  companyName,
  eventName,
  clientName,
  contacts,
  onCancel,
  onSent,
}: {
  base: string;
  companyName: string;
  eventName: string;
  clientName: string;
  contacts: MessageContact[];
  onCancel: () => void;
  onSent: (heldReminders: boolean) => void;
}) {
  const primary = contacts.find((c) => c.isPrimary) ?? contacts[0];
  const others = contacts.filter((c) => c.id !== primary?.id);

  const [templates, setTemplates] = useState<Template[]>([]);
  /* Resolved server-side for this sponsorship. The browser no longer has its
     own idea of what a field is, so it cannot drift from the send path. */
  const [fields, setFields] = useState<Record<string, string | null>>({});
  /** What each field is called, for the Insert Field menu. */
  const [fieldInfo, setFieldInfo] = useState<Record<string, string>>({});
  const [unavailable, setUnavailable] = useState<Record<string, string>>({});
  /** Days to the logo deadline, so the hold can say what it cancels. */
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const hlRef = useRef<HTMLDivElement>(null);
  const [subject, setSubject] = useState(`Your logo for ${eventName}`);
  const [body, setBody] = useState('');

  /* Recomputed as you type, so what is shown is always what would go now. */
  const preview = useMemo(() => previewBody(body, fields), [body, fields]);
  const droppedCount = preview.filter((p) => p.dropped).length;

  /** Puts a field where the caret is, which is the only placement that does
   *  not make somebody retype the sentence around it. */
  function insertField(name: string) {
    const el = bodyRef.current;
    const token = `{${name}}`;
    if (!el) {
      setBody((b) => b + token);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setBody(el.value.slice(0, start) + token + el.value.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }
  /* Everyone checked to begin with, which is what people expect, and each one
     unticking independently, which is what they actually need. */
  const [cc, setCc] = useState<string[]>(others.map((c) => c.id));
  const [hold, setHold] = useState(true);

  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    void fetch(`${base}/message`)
      .then((r) => (r.ok ? r.json() : { templates: [], fields: {} }))
      .then((b: {
        templates: Template[];
        fields?: Record<string, string | null>;
        fieldInfo?: Record<string, string>;
        unavailable?: Record<string, string>;
        previewOnly?: Record<string, string>;
        daysUntilDeadline?: number | null;
      }) => {
        setTemplates(b.templates);
        /* The preview-only values sit alongside the real ones so the box can
           show what {portal_link} will become without ever holding a working
           link: the real one is minted at send. */
        setFields({ ...(b.fields ?? {}), ...(b.previewOnly ?? {}) });
        setFieldInfo(b.fieldInfo ?? {});
        setUnavailable(b.unavailable ?? {});
        setDaysLeft(b.daysUntilDeadline ?? null);
      })
      .catch(() => {
        setTemplates([]);
        setFields({});
      });
  }, [base]);

  /**
   * Fills the form from a template, resolving every field this sponsorship has
   * a value for.
   *
   * SUBSTITUTION HAPPENS HERE, when the form is seeded, rather than when the
   * message sends. That is the right way round for something a person then
   * edits: the words in the box are the words the sponsor gets, so anything
   * that reads badly can be fixed before pressing send. Automated mail resolves
   * at send time instead, because nobody is watching it.
   *
   * The values come from the server. This used to substitute {event} and
   * {contact} with two regexes of its own, which meant the other nine fields in
   * the vocabulary were pasted in literally and sent that way.
   *
   * A field with no value is LEFT VISIBLE rather than blanked. The automated
   * path drops the whole paragraph in that case, which is right when no one is
   * looking; here somebody is about to read it, and a gap they can see beats a
   * sentence quietly missing a word.
   */
  function apply(name: string) {
    const template = templates.find((t) => t.name === name);
    if (!template) return;

    const fill = (text: string) =>
      text.replace(/\{[a-z_]+\}/g, (token) => {
        const value = fields[token.slice(1, -1)];
        return typeof value === 'string' && value.trim() ? value : token;
      });

    setSubject(fill(template.subject));
    setBody(fill(template.body));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setProblem(null);

    const res = await fetch(`${base}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subject: subject.trim(),
        body: body.trim(),
        ccContactIds: cc,
        holdReminders: hold,
      }),
    });

    setSaving(false);

    if (res.ok) {
      onSent(hold);
      return;
    }

    const payload = (await res.json().catch(() => ({}))) as { error?: string };
    setProblem(payload.error ?? 'That did not send.');
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="panel-head">
        <h3>Message {companyName}</h3>
      </div>

      <div className="panel-body">
        <div className="callout">
          <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <div>
            Sends as <b>{clientName}</b>, with replies going to them. It appears on this sponsor's
            timeline.
          </div>
        </div>

        <div className="fg">
          <label className="fl">To</label>
          <p className="fdesc" style={{ marginTop: 0 }}>
            {primary ? `${primary.name} · ${primary.email}` : 'No contact on file'}
          </p>
        </div>

        {others.length > 0 && (
          <div className="fg">
            <label className="fl"><T k="message.field.cc" d="Also Copy" /></label>
            <div className="cclist">
              {others.map((contact) => (
                <label key={contact.id} className="fcheck">
                  <input
                    type="checkbox"
                    checked={cc.includes(contact.id)}
                    onChange={(e) =>
                      setCc((prev) =>
                        e.target.checked
                          ? [...prev, contact.id]
                          : prev.filter((id) => id !== contact.id),
                      )
                    }
                  />
                  <span>
                    {contact.name}
                    <span className="fdesc">{contact.email}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {templates.length > 0 && (
          <div className="fg">
            <label className="fl" htmlFor="template"><T k="message.field.template" d="Start From" /></label>
            <select
              id="template"
              className="fi"
              defaultValue=""
              onChange={(e) => apply(e.target.value)}
            >
              <option value="">Write your own</option>
              {templates.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
            <p className="fdesc"><T k="message.field.templateHint" d="A starting point. Edit anything before sending." /></p>
          </div>
        )}

        <div className="fg">
          <label className="fl" htmlFor="subject"><T k="message.field.subject" d="Subject" /></label>
          <input
            id="subject"
            className="fi"
            required
            maxLength={200}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className="fg">
          <label className="fl" htmlFor="body"><T k="message.field.body" d="Message" /></label>

          {/* THE SAME MENU THE EMAIL TEMPLATES HAVE. Fields have always
              resolved in a hand-written message — the send path runs them
              through the identical resolver — but nothing on this screen said
              so or listed what exists, so the feature was only usable by
              somebody who already knew the vocabulary. */}
          {Object.keys(fieldInfo).length > 0 && (
            <div className="tpl-bar">
              <div className="tpl-menu-wrap">
                <button
                  type="button"
                  className="btn-compact btn-compact--quiet"
                  aria-haspopup="true"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((v) => !v)}
                >
                  <T k="message.insertField" d="Insert Field" />
                </button>
                {menuOpen && (
                  <div className="tpl-menu" role="menu">
                    <p className="tpl-menu-label">
                      <T k="message.fields.available" d="Available in this message" />
                    </p>
                    {Object.entries(fieldInfo).map(([name, label]) => (
                      <button
                        key={name}
                        type="button"
                        role="menuitem"
                        className="tpl-mi"
                        onClick={() => {
                          insertField(name);
                          setMenuOpen(false);
                        }}
                      >
                        <code>{`{${name}}`}</code>
                        <span>{label}</span>
                      </button>
                    ))}

                    {Object.keys(unavailable).length > 0 && (
                      <>
                        <p className="tpl-menu-label">
                          <T k="message.fields.unavailable" d="Not available here" />
                        </p>
                        {Object.entries(unavailable).map(([name, why]) => (
                          <div key={name} className="tpl-mi tpl-mi--dead">
                            <code>{`{${name}}`}</code>
                            <span>{why}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
              <span className="tpl-barnote">
                <T k="message.field.bodyHint" d="Blank lines become paragraphs." />
              </span>
            </div>
          )}

          {/* The fields marked where they sit, so it is obvious which parts of
              a sentence are filled in and which are fixed. */}
          <div className="tpl-ed">
            <div
              className="tpl-hl"
              ref={hlRef}
              aria-hidden="true"
            >
              {segments(body).map((seg, i) =>
                seg.token ? <mark key={i}>{seg.text}</mark> : <span key={i}>{seg.text}</span>,
              )}
              {'\n'}
            </div>
            <textarea
              id="body"
              ref={bodyRef}
              className="fi ft"
              required
              rows={7}
              maxLength={4000}
              value={body}
              onScroll={(e) => {
                if (hlRef.current) hlRef.current.scrollTop = e.currentTarget.scrollTop;
              }}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`Hi ${primary?.name ?? 'there'},`}
            />
          </div>
        </div>

        {/* WHAT ACTUALLY SENDS, which is not always what is in the box above.
            A paragraph whose field has no value for this sponsor is left out
            entirely by the send path, and before this there was nothing to say
            so: somebody wrote a sentence, read it back, pressed Send, and it
            never arrived. */}
        {body.trim() && (
          <div className="fg">
            <p className="fl">
              <T k="message.preview.title" d="What they will receive" />
            </p>
            <div className="tpl-preview">
              <p className="tpl-preview-subject">{previewSubject(subject, fields)}</p>
              {preview.map((p, i) => (
                <p
                  key={i}
                  className={p.dropped ? 'tpl-preview-p tpl-preview-p--gone' : 'tpl-preview-p'}
                >
                  {p.text}
                </p>
              ))}
            </div>

            {droppedCount > 0 && (
              <p className="msg-dropped">
                <b>
                  {droppedCount === 1
                    ? 'One paragraph will not be sent.'
                    : `${droppedCount} paragraphs will not be sent.`}
                </b>{' '}
                <T
                  k="message.preview.dropped"
                  d="A paragraph whose field has no value for this sponsor is left out entirely. Rewrite it, or take the field out."
                />
              </p>
            )}
          </div>
        )}

        <div className="rem-pref" style={{ marginTop: 0 }}>
          <div>
            <b><T k="message.hold.title" d="Hold automatic reminders for a week" /></b>
            {/* SAYS WHAT A WEEK ACTUALLY COSTS. The hold is seven days whatever
                the deadline is, so on a sponsorship three days out it quietly
                cancels every remaining reminder rather than delaying one. The
                toggle said only that it stopped a reminder tomorrow morning. */}
            <span>
              {hold && daysLeft !== null && daysLeft <= 7 ? (
                daysLeft <= 0
                  ? 'The deadline has passed, so no further reminders were scheduled anyway.'
                  : `The deadline is ${daysLeft === 1 ? 'tomorrow' : `in ${daysLeft} days`}, so this cancels every remaining reminder for this sponsor rather than delaying one.`
              ) : (
                <T k="message.hold.hint" d="So this is not followed by a reminder tomorrow morning." />
              )}
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={hold}
            aria-label="Hold automatic reminders for a week"
            className={`toggle${hold ? ' on' : ''}`}
            onClick={() => setHold((v) => !v)}
          />
        </div>

        {problem && <p className="auth-error">{problem}</p>}

        <div className="factions">
          <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-compact" disabled={saving || !body.trim()}>
            {saving ? 'Sending…' : 'Send Message'}
          </button>
        </div>
      </div>
    </form>
  );
}
